import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, UserPlus, CheckCircle2, ArrowLeft } from "lucide-react";
import portalApi from "../../lib/portalApi";

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-sm font-semibold text-neutral-700">
      {label}{required && <span className="text-[var(--pop-pink,#e5006d)]"> *</span>}
    </label>
    {children}
  </div>
);

const inputCls = "mt-1 w-full rounded-xl border border-neutral-300 px-4 py-2.5 outline-none focus:border-black";

export default function PortalRegister() {
  const nav = useNavigate();
  const [f, setF] = useState({
    company: "", first_name: "", last_name: "", email: "", phone: "",
    street: "", zip: "", city: "", country: "DE", vat_id: "", password: "", password2: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onZip = (v) => {
    set("zip", v);
    if (/^\d{5}$/.test(v)) {
      portalApi.get(`/geo/plz/${v}`)
        .then((r) => { if (r.data && r.data.found) setF((s) => ({ ...s, city: r.data.city, country: r.data.country || "DE" })); })
        .catch(() => {});
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.company || !f.first_name || !f.last_name || !f.email) { setErr("Bitte alle Pflichtfelder ausfüllen."); return; }
    if (f.password.length < 6) { setErr("Passwort mindestens 6 Zeichen."); return; }
    if (f.password !== f.password2) { setErr("Passwörter stimmen nicht überein."); return; }
    setBusy(true);
    try {
      await portalApi.post("/register", {
        company: f.company, first_name: f.first_name, last_name: f.last_name,
        email: f.email, phone: f.phone, street: f.street, zip: f.zip, city: f.city,
        country: f.country, vat_id: f.vat_id, password: f.password,
      });
      setDone(true);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Registrierung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f10] px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl text-center" data-testid="portal-register-done">
          <CheckCircle2 size={56} className="text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Registrierung eingegangen</h1>
          <p className="text-neutral-600 text-sm">
            Vielen Dank! Wir prüfen Ihre Angaben und schalten Ihren Zugang frei.
            Sobald Ihr Konto aktiviert ist, erhalten Sie eine E-Mail mit dem Login-Link.
          </p>
          <button onClick={() => nav("/portal/login")} data-testid="portal-register-to-login"
                  className="mt-6 w-full rounded-xl bg-black text-white py-3 font-bold hover:bg-neutral-800">
            Zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f0f10] px-4 py-10" data-testid="portal-register">
      <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-11 w-11 rounded-2xl bg-[var(--pop-yellow,#ffd400)] flex items-center justify-center">
            <ShoppingBag size={22} className="text-black" />
          </div>
          <div>
            <div className="font-black text-xl tracking-tight">POPCRN</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Kundenportal</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold mt-6 mb-1">Registrieren</h1>
        <p className="text-sm text-neutral-500 mb-6">Legen Sie Ihr Kundenkonto an. Nach Prüfung schalten wir den Portal-Zugang frei.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Firma" required>
            <input value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="reg-company" className={inputCls} required />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Vorname" required>
              <input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-first-name" className={inputCls} required />
            </Field>
            <Field label="Nachname" required>
              <input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-last-name" className={inputCls} required />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="E-Mail" required>
              <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" className={inputCls} required />
            </Field>
            <Field label="Telefon">
              <input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone" className={inputCls} />
            </Field>
          </div>
          <Field label="Straße & Nr.">
            <input value={f.street} onChange={(e) => set("street", e.target.value)} data-testid="reg-street" className={inputCls} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="PLZ">
              <input value={f.zip} onChange={(e) => onZip(e.target.value)} data-testid="reg-zip" className={inputCls} inputMode="numeric" />
            </Field>
            <Field label="Ort">
              <input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="reg-city" className={inputCls} />
            </Field>
            <Field label="Land">
              <input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="reg-country" className={inputCls} />
            </Field>
          </div>
          <Field label="USt-ID (optional)">
            <input value={f.vat_id} onChange={(e) => set("vat_id", e.target.value)} data-testid="reg-vat" className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Passwort" required>
              <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" className={inputCls} required />
            </Field>
            <Field label="Passwort wiederholen" required>
              <input type="password" value={f.password2} onChange={(e) => set("password2", e.target.value)} data-testid="reg-password2" className={inputCls} required />
            </Field>
          </div>

          {err && <div className="text-sm text-red-600 font-medium" data-testid="reg-error">{err}</div>}

          <button type="submit" disabled={busy} data-testid="reg-submit"
                  className="w-full rounded-xl bg-[var(--pop-pink,#e5006d)] text-white py-3 font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50">
            <UserPlus size={18} /> {busy ? "Senden…" : "Registrierung absenden"}
          </button>
        </form>

        <button type="button" onClick={() => nav("/portal/login")} data-testid="reg-back-login"
                className="mt-4 w-full flex items-center justify-center gap-1 text-sm text-neutral-500 hover:text-black">
          <ArrowLeft size={14} /> Zurück zur Anmeldung
        </button>
      </div>
    </div>
  );
}
